import { usePrevious } from 'ahooks';
import { shallowEqual } from 'react-redux';

/**
 * El objetivo principal de este hook es permitir
 * investigar el performance de la aplicación.
 *
 * El método es: se registran componentes en shouldDebug
 * con un nombre una propiedad shouldDebugProps: boolean.
 *
 * Luego, desde el componente se coloca y se deja
 * colocado el hook useRegisteredRenderDebug, al cual
 * se le pasa el nombre de la propiedad colocada en
 * shouldDebug (no el consoleDisplayName, sino el
 * nombre de la propiedad).
 *
 * Una vez hecho esto, es posible prender y apagar el
 * debug de ese componente en la consola, todo desde un
 * mismo lugar.
 */

/**
 * Se puede establecer una lista de nombres que se
 * desean ignorar en el debug, apagando el debug
 * proveniente de ese name.
 */
const ignoredNames: string[] = [];
const isEnabled = true;

/**
 * Permite concentrar en un solo lugar todos los
 * elementos que son candidatos a debug de performance
 * en un mismo lugar.
 */
export const shouldDebudg = {
  apiaModal: {
    consoleDisplayName: 'ApiaModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  addUserSecurityItemModal: {
    consoleDisplayName: 'AddUserSecurityItemModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  confirmModal: {
    consoleDisplayName: 'ConfirmModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  dataGrid: {
    consoleDisplayName: 'DataGrid.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  dataGridFooter: {
    consoleDisplayName: 'DataGrid > Footer.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  poolsModal: {
    consoleDisplayName: 'PoolsModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  orgRolesModal: {
    consoleDisplayName: 'OrgRolesModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },

  useModal: {
    consoleDisplayName: 'useModal.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
  usePapas: {
    consoleDisplayName: 'usePapas.tsx',
    shouldDebugProps: false,
    shouldDebugEachRender: false,
  },
};

/**
 * Permite insertar un debugger en cualquier componente
 * que se ejecuta condicionalmente, sin la necesidad
 * de preocuparse por el hecho de que los hooks no se
 * pueden ejecutar condicinalmente.
 *
 * Acepta una condición que en caso de evaluar a true,
 * hace que el debugger actúe, mostrando en la consola
 * qué propiedades cambiaron y cuál es su valor anterior
 * y el actual.
 *
 * @param condition Un booleano que en caso de ser true
 * hace que el hook escriba en la consola los cambios.
 *
 * @param name Un nombre para mostrar en la consola.
 *
 * @param props Un Record<string,unknown> que contiene
 * las propiedades que se desea monitorear.
 */
export default function useRenderDebug(
  condition: boolean,
  name: string,
  props: Record<string, unknown>,
  shouldDebugEachRender?: boolean,
) {
  if (shouldDebugEachRender) {
    console.log('%c', 'font-size: 6vh');
    console.log(`%c${name}`, 'color: yellow');
  }

  const previousProps = usePrevious(props);
  if (!previousProps) return;
  if (ignoredNames.includes(name) || !isEnabled) return;

  const changedProps: Record<string, unknown> = {};
  Object.entries(props).forEach(([key, value]) => {
    if (value !== previousProps[key])
      changedProps[key] = {
        prev: previousProps[key],
        new: value,
      };
  });

  if (condition && !shallowEqual(props, previousProps)) {
    if (!shouldDebugEachRender) {
      console.log('%c', 'font-size: 6vh');
      console.log(`%c${name}`, 'color: yellow');
    }
    console.log(changedProps);
  }
}

/**
 * **Este componente es una facilidad de useRenderDebug
 * que permite tener concentrados en un mismo lugar los
 * nombres y el estado de debug de los componentes de
 * la aplicación.**
 *
 * **Para registrar un nuevo componente, debe agregarse
 * un registro en useRenderDebug.ts -> shouldRender**
 *
 * Permite insertar un debugger en cualquier componente
 * que se ejecuta condicionalmente, sin la necesidad
 * de preocuparse por el hecho de que los hooks no se
 * pueden ejecutar condicinalmente.
 *
 * Acepta una condición que en caso de evaluar a true,
 * hace que el debugger actúe, mostrando en la consola
 * qué propiedades cambiaron y cuál es su valor anterior
 * y el actual.
 *
 * @param name El nombre del componente
 *
 * @param props Un Record<string,unknown> que contiene
 * las propiedades que se desea monitorear.
 */
export function useRegisteredRenderDebug(
  name: keyof typeof shouldDebudg,
  props: Record<string, unknown>,
) {
  useRenderDebug(
    shouldDebudg[name].shouldDebugProps,
    shouldDebudg[name].consoleDisplayName,
    props,
    shouldDebudg[name].shouldDebugEachRender,
  );
}
